<template>
  <div
    class="menu-item"
    :class="{
      'menu-item--dropdown-active': dropdownState,
      'menu-item--highlight-exact-active': highlightExactActive,
      'menu-item--disabled': disabled,
      'menu-item--in-group': inGroup,
      'menu-item--submenu': isSubmenu,
    }"
  >
    <tm-icon
      v-if="showDragIndicator"
      name="drag_indicator"
      class="menu-item__drag show-on-hover"
    />

    <div
      v-if="icon"
      class="menu-item__icon"
    >
      <slot name="icon">
        <tm-icon :name="icon" />
      </slot>
    </div>

    <div
      class="menu-item__text"
      :title="tooltip || title"
    >
      <span>
        {{ title }}
      </span>
    </div>
    <div
      class="menu-item__right-col d-flex"
      :class="{ 'menu-item__right-col--badge': badge }"
    >
      <tm-menu-item-badge
        v-if="badge"
        class="menu-item__badge"
        :badge="badge"
      />
      <slot name="menu-item-counter">
        <div
          class="menu-item__counter"
          :class="{ 'menu-item__counter_hide': actions, 'menu-item__counter-red': redCounter && number }"
        >
          <span v-if="isLoadingNumber || number === null">&hellip;</span>
          <span v-else-if="isLimitExceeded">{{ formatNumber(number) }}+</span>
          <span v-else-if="number || number === 0">
            {{ formatNumber(number) }}
          </span>
        </div>
      </slot>

      <slot name="append" />

      <tm-icon
        v-if="rightIcon"
        v-bind="rightIcon"
      />

      <div
        v-if="actions"
        class="menu-item-sidebar-menu-actions"
      >
        <tm-sidebar-menu-actions
          v-model:dropdown-state="dropdownState"
          :actions="actions"
          @action-click="$emit('action-click', $event)"
        />
      </div>
    </div>
    <slot name="after-link-content" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from '@/composition/vue/compositionApi'
import TmSidebarMenuActions from '@/components/layout/pageContent/sidebar/PageContentSidebarItemActions.vue'
import TmIcon from '@/components/shared/TmIcon.vue'
import type {
  PageContentSidebarItemAction,
  PageContentSidebarLink,
} from '@/components/layout/pageContent/sidebar/types'
import { getNumericService } from '@/core/container/ioc'
import type { TmIconType } from '@/components/shared/types'
import type { IconName } from '@/assets/icons/icons'
import TmMenuItemBadge from '@/components/shared/badge/TmMenuItemBadge.vue'

const props = defineProps<{
  icon?: IconName
  img?: string
  rightIcon?: TmIconType
  title?: string
  number?: number
  numberLimit?: number
  isLoadingNumber?: boolean
  actions?: PageContentSidebarItemAction[]
  tooltip?: string
  badge?: PageContentSidebarLink['badge']
  highlightExactActive?: boolean
  showDragIndicator?: boolean
  disabled?: boolean
  inGroup?: boolean
  isSubmenu?: boolean
  redCounter?: boolean
}>()
const emit = defineEmits<{
  'action-click': [PageContentSidebarItemAction]
  'lock-sidebar': [boolean]
}>()

const dropdownState = ref(false)
watch(dropdownState, (value) => emit('lock-sidebar', value))

const numericService = getNumericService()
const formatNumber = (val?: number) => {
  return val ? numericService.formatNumber(val) : 0
}

const isLimitExceeded = computed(() => {
  return props.number && props.numberLimit && props.number > props.numberLimit
})
</script>

<style lang="scss" scoped>
.menu-item {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 16px;
  line-height: 20px;
  font-size: $font-size-root;
  font-weight: $semi-bold-font;
  border-radius: 6px;
  outline: none;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid $blue400 !important;
    outline-offset: 2px;
    z-index: 1;
  }
  &:not(:hover):not(:focus-within):not(.menu-item--dropdown-active),
  &:focus:not(:focus-visible):not(:hover):not(.menu-item--dropdown-active) {
    .show-on-hover {
      opacity: 0;
    }
    .menu-item-sidebar-menu-actions {
      display: none;
    }
  }
  &:hover:not(.menu-item--disabled, .menu-item--submenu),
  &--dropdown-active {
    background: $page-content-sidebar-item-hover;
    .menu-item__counter_hide {
      display: none;
    }
  }

  &--in-group {
    padding-left: 42px;
  }

  &--disabled {
    cursor: auto;
  }
  &__drag {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    color: $light;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
    @media (max-width: $breakpoint-md-max) {
      display: none !important;
    }
  }
  @media (max-width: $breakpoint-md-max) {
    .menu-item-sidebar-menu-actions {
      display: initial !important;
    }
  }

  &--in-group &__drag {
    left: 8px;
  }

  &__icon {
    display: flex;
    margin-right: 8px;
  }

  &__text {
    display: flex;
    flex-flow: column;
    overflow: hidden;
    flex-grow: 1;
    max-width: 100%;
    font-weight: $medium-font;
    white-space: nowrap !important;
    margin-right: 6px;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &--submenu &__text {
    font-weight: $semi-bold-font;
  }

  &--in-group &__text {
    font-weight: $medium-font;
  }

  &__right-col {
    flex-shrink: 0;

    &--badge {
      width: auto;
    }
  }
  &__counter {
    flex-grow: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: $sm-font;
    font-weight: $bold-font;
    color: $gray6;

    &-red {
      justify-content: center;
      height: 16px;
      min-width: 20px;
      line-height: 16px;
      background: $error;
      border-radius: $border-radius-lg;
      color: $white;
      padding: 0 7px;
    }
    @media (max-width: $breakpoint-md-max) {
      display: none;
    }
  }

  &__badge {
    flex-grow: 1;
    align-self: center;
  }
}
.menu-item:has(.menu-item__counter-red) {
  padding-right: 10px;
}
.active-link .menu-item:not(.menu-item--highlight-exact-active),
.router-link-exact-active .menu-item--highlight-exact-active,
.active-item.menu-item--highlight-exact-active {
  background: $page-content-sidebar-item-hover;
  .menu-item__counter:not(.menu-item__counter-red),
  .menu-item__icon,
  .menu-item__text {
    color: $blue;
  }
}

.menu-item-sidebar-menu-actions {
  width: 20px;
}
</style>
